import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    DollarSign,
    ShoppingCart,
    Tag,
    AlertCircle,
    TrendingUp
} from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { GlassButton } from '../components/ui/GlassButton';
import { StatCard } from '../components/dashboard/StatCard';
import { RevenueBotList } from '../components/dashboard/RevenueBotList';
import { GroupMonitor } from '../components/dashboard/GroupMonitor';
import { DateFilter } from '../components/dashboard/DateFilter';
import { DashboardChart } from '../components/dashboard/DashboardChart';

// Interfaces
interface DashboardProps {
    selectedAccountId: number | string | null;
    onOpenIDFinder: () => void;
}

interface ChartDataPoint {
    date: string;
    revenue: number;
    sales: number;
}

interface RevenueData {
    total_revenue: number;
    total_sales: number;
    average_ticket: number;
    chart_data: ChartDataPoint[];
    bots: Array<{
        bot_id: number;
        bot_name: string;
        total_revenue: number;
        plans: Array<any>;
    }>;
}

const PLAN_SLUG_LABELS: Record<string, string> = {
    starter: 'Starter',
    pro: 'Pro Scale',
    lifetime: 'Lifetime',
    free: 'Gratuito'
};

function planSlugToLabel(slug: string | null): string {
    if (!slug) return 'Gratuito';
    return PLAN_SLUG_LABELS[slug] || slug;
}

export const Dashboard: React.FC<DashboardProps> = ({ selectedAccountId, onOpenIDFinder }) => {
    const navigate = useNavigate();
    const { planType, hasActiveSubscription } = useAuth();

    // States
    const [revenueData, setRevenueData] = useState<RevenueData | null>(null);
    const [groups, setGroups] = useState<any[]>([]);
    const [selectedGroup, setSelectedGroup] = useState<number | null>(null);
    const [groupData, setGroupData] = useState<any>(null);
    const [groupLoading, setGroupLoading] = useState(false);

    const [initialLoading, setInitialLoading] = useState(true);
    const [fetchError, setFetchError] = useState(false);

    // Date Filters
    const [startDate, setStartDate] = useState<string | null>(null);
    const [endDate, setEndDate] = useState<string | null>(null);

    // Initial Date Logic (Month)
    useEffect(() => {
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        setStartDate(start.toISOString());
    }, []);

    const abortControllerRef = useRef<AbortController | null>(null);

    // Quando mudar o filtro de data (que seta startDate/endDate), carrega a API.
    useEffect(() => {
        // Se startDate ainda é null e não foi recém-forçado na subida (se for filter="all" start is null) vamos continuar. 
        // Aqui assumimos que se startDate mudou para ser usado, disparamos a request.
        let mounted = true;
        abortControllerRef.current = new AbortController();

        const fetchAnalytics = async () => {
            // Opcional abort anterior se for rapid-click
            try {
                setInitialLoading(true);
                setFetchError(false);

                // Fetch metrics by date range
                const params: any = {};
                if (startDate) params.startDate = startDate;
                if (endDate) params.endDate = endDate;

                const revenueRes = await api.get('/analytics/revenue', {
                    params,
                    signal: abortControllerRef.current?.signal
                });

                if (mounted) {
                    setRevenueData(revenueRes.data);
                }
            } catch (error: any) {
                if (error.name !== 'CanceledError' && mounted) {
                    console.error('Erro crítico no dashboard:', error);
                    setFetchError(true);
                }
            } finally {
                if (mounted) setInitialLoading(false);
            }
        };

        fetchAnalytics();

        return () => {
            mounted = false;
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [startDate, endDate]);


    // Fetch Groups only once
    useEffect(() => {
        const loadGroupsInfo = async () => {
            try {
                const res = await api.get('/groups');
                const data = res.data;
                setGroups(data);
                if (data && data.length > 0 && !selectedGroup) {
                    setSelectedGroup(data[0].id);
                }
            } catch (e) {
                console.error('Erro buscando grupos', e);
            }
        };
        loadGroupsInfo();
    }, []);

    // Load group data based on selected group
    useEffect(() => {
        if (!selectedGroup) return;
        let mounted = true;

        const loadGroupAnalytics = async () => {
            try {
                setGroupLoading(true);
                const res = await api.get(`/analytics/group-members/${selectedGroup}`);
                if (mounted) setGroupData(res.data);
            } catch (error) {
                console.error('Erro ao carregar dados do grupo:', error);
                if (mounted) setGroupData(null);
            } finally {
                if (mounted) setGroupLoading(false);
            }
        };

        loadGroupAnalytics();

        const intGroup = setInterval(loadGroupAnalytics, 30000);
        return () => {
            mounted = false;
            clearInterval(intGroup);
        };
    }, [selectedGroup]);


    const handleFilterChange = (newStart: string | null, newEnd: string | null) => {
        setStartDate(newStart);
        setEndDate(newEnd);
    };

    // Error State Fallback
    if (fetchError && !initialLoading) {
        return (
            <div className="dashboard-view" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{
                    background: 'var(--glass-bg)',
                    backdropFilter: 'blur(12px)',
                    border: '1px solid rgba(239, 68, 68, 0.2)',
                    borderRadius: 'var(--radius)',
                    padding: '40px',
                    textAlign: 'center',
                    maxWidth: '400px'
                }}>
                    <AlertCircle size={48} color="#ef4444" style={{ margin: '0 auto 16px' }} />
                    <h2 style={{ fontSize: '18px', color: '#fff', marginBottom: '8px' }}>Falha de Conexão</h2>
                    <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '24px' }}>
                        Não foi possível carregar os dados do dashboard. Verifique sua conexão.
                    </p>
                    <GlassButton onClick={() => window.location.reload()} style={{ width: '100%' }}>
                        Tentar Novamente
                    </GlassButton>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard-view">
            {/* Top Bar: título e plano assinado na mesma linha */}
            <div className="top-bar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', gap: '16px', flexWrap: 'wrap' }}>
                <h1 className="page-title" style={{ margin: 0 }}>Dashboard</h1>
                <div
                    className="plan-badge"
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '6px 12px',
                        borderRadius: '8px',
                        borderLeft: `3px solid ${hasActiveSubscription ? 'var(--accent, #ff6a00)' : 'var(--border-dark, #333)'}`,
                        background: hasActiveSubscription ? 'rgba(255, 106, 0, 0.08)' : 'rgba(255,255,255,0.03)',
                        color: hasActiveSubscription ? 'var(--accent, #ff6a00)' : 'var(--text-muted, #888)',
                        fontSize: '13px',
                        fontWeight: 600,
                        fontFamily: 'var(--font-sans, "Plus Jakarta Sans", sans-serif)',
                        whiteSpace: 'nowrap'
                    }}
                >
                    <span style={{ fontWeight: 500, color: 'inherit', opacity: 0.9 }}>Plano assinado:</span>
                    <span>{planSlugToLabel(planType)}</span>
                </div>
            </div>

            {/* Date Filters Component */}
            <DateFilter onFilterChange={handleFilterChange} />

            {/* Smaller Stats Grid (KPIs de Venda) */}
            <div className="dashboard-stats-grid" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                gap: '16px',
                marginBottom: '24px'
            }}>
                <StatCard
                    title="Faturamento"
                    value={`R$ ${(revenueData?.total_revenue || 0).toFixed(2)}`}
                    sublabel="No período"
                    MainIcon={DollarSign}
                    SubIcon={TrendingUp}
                    colorBase="#22c55e"
                    colorRgba="rgba(34, 197, 94, 0.1)"
                    loading={initialLoading}
                />

                <StatCard
                    title="Vendas"
                    value={revenueData?.total_sales || 0}
                    sublabel="Transações pagas"
                    MainIcon={ShoppingCart}
                    SubIcon={TrendingUp}
                    colorBase="#3b82f6"
                    colorRgba="rgba(59, 130, 246, 0.1)"
                    loading={initialLoading}
                />

                <StatCard
                    title="Ticket Médio"
                    value={`R$ ${(revenueData?.average_ticket || 0).toFixed(2)}`}
                    sublabel="Média por venda"
                    MainIcon={Tag}
                    SubIcon={TrendingUp}
                    colorBase="#ff6a00"
                    colorRgba="rgba(255, 106, 0, 0.1)"
                    loading={initialLoading}
                />
            </div>

            {/* Main Content Grid: Chart em destaque */}
            <DashboardChart
                data={revenueData?.chart_data || []}
                loading={initialLoading}
            />

            {/* Bottom Grid: Bots Revenue & Group Monitor */}
            <div className="dashboard-main-grid" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
                gap: '24px',
                minHeight: '300px'
            }}>
                <RevenueBotList
                    data={revenueData?.bots || []}
                    loading={initialLoading}
                />

                <GroupMonitor
                    groups={groups}
                    selectedGroup={selectedGroup}
                    onGroupChange={setSelectedGroup}
                    groupData={groupData}
                    loading={initialLoading || groupLoading}
                />
            </div>
        </div>
    );
};
